import NavigationBackHeader from '@app/components/presentational/common/NavigationBackHeader/NavigationBackHeader';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import {
  Box,
  Button,
  IconButton,
  Icons,
  Menu,
  MenuItem,
  Modal,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import React, { MouseEvent, useCallback, useRef, useState } from 'react';
import { createStyles } from './SubmissionsSidebar.styles';
import ManageExtension from '../ManageExtension/ManageExtension';
import { ExtensionUser } from '../ManageExtension/ManageExtension.types';
import ExtensionItem from './ExtensionItem';
import { useSelector } from 'react-redux';
import { accessingCurrentSpaceAsStaff } from '@core/selectors/classroom';
import {
  CANCEL_EXTENSION,
  DELETE_EXTENSION,
  EXTENSION_DELETE_CONFIRMATION_TITLE,
  SURE_DELETE_EXTENSION_RESPONSIVE,
} from '@app/constants/texts';

type SubmissionAssignment = {
  description?: string;
  endDate?: string;
  gradesReleasedAt?: string;
  gradeType?: string;
  groupSet?: string;
  id: string;
  isHidden?: boolean;
  isAnonymised?: boolean;
  objectId?: string;
  parentAssignmentId?: string;
  provider?: string;
  shouldCloseOnDueDate?: boolean;
  startDate?: string;
  studentsCount?: number;
  students?: string[];
  submissions?: string[];
  title: string;
};

type Props = {
  accessToken?: string;
  assignment?: SubmissionAssignment;
  assignmentCreationError?: any;
  assignmentUpdateError?: any;
  backRoute: string;
  canCreateExtensions: boolean;
  canEditExtensions: boolean;
  createExtension: (params: any) => Promise<SubmissionAssignment | null>;
  editExtension: (params: any) => Promise<SubmissionAssignment | null>;
  extensions: SubmissionAssignment[];
  loadStudents: (spaceId: string) => Promise<any[]>;
  onExtensionSaved: (savedExtension?: SubmissionAssignment) => Promise<void>;
  onSelectExtension: (extension: SubmissionAssignment) => void;
  resetExtensionErrors: () => void;
  selectedExtensionId: string;
  spaceId: string;
  user: ExtensionUser | null;
};

const SubmissionSidebar = ({
  accessToken,
  assignment,
  assignmentCreationError,
  assignmentUpdateError,
  backRoute,
  canCreateExtensions,
  canEditExtensions,
  createExtension,
  editExtension,
  extensions,
  loadStudents,
  onExtensionSaved,
  onSelectExtension,
  resetExtensionErrors,
  selectedExtensionId,
  spaceId,
  user,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isDark = theme.palette.mode === 'dark';
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const assignmentTitle = assignment?.title;
  const [titleOverflowRef, isTitleWidthOverflowing] =
    useIsOverflowing<HTMLHeadingElement>();
  const [isTitleHeightOverflowing, setIsTitleHeightOverflowing] =
    useState(false);
  const titleResizeObserver = useRef<ResizeObserver | null>(null);
  const setTitleRef = useCallback(
    (node: HTMLHeadingElement | null) => {
      titleOverflowRef(node);
      if (titleResizeObserver.current) {
        titleResizeObserver.current.disconnect();
      }
      titleResizeObserver.current = null;
      if (!node) return;

      const checkHeightOverflow = () =>
        setIsTitleHeightOverflowing(node.scrollHeight > node.clientHeight);
      checkHeightOverflow();
      titleResizeObserver.current = new ResizeObserver(checkHeightOverflow);
      titleResizeObserver.current.observe(node);
    },
    [titleOverflowRef]
  );
  const isTitleOverflowing =
    isTitleWidthOverflowing || isTitleHeightOverflowing;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedExtension, setSelectedExtension] =
    useState<SubmissionAssignment>();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuExtension, setMenuExtension] = useState<SubmissionAssignment>();
  const [openDeleteExtensionModal, setOpenDeleteExtensionModal] =
    useState<boolean>(false);
  const [isDeletingExtension, setIsDeletingExtension] =
    useState<boolean>(false);

  const closeExtension = () => {
    setIsOpen(false);
    setSelectedExtension(undefined);
  };

  const openMenu = (
    event: MouseEvent<HTMLButtonElement>,
    extension: SubmissionAssignment
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuExtension(extension);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuExtension(undefined);
  };

  const openEditExtension = () => {
    if (!menuExtension || !canEditExtensions) return;
    setSelectedExtension(menuExtension);
    setIsOpen(true);
    closeMenu();
  };

  // Handle delete extension
  const handleDeleteExtension = async () => {
    let extensionForDeletion = menuExtension;
    if (!extensionForDeletion || !accessToken || !spaceId || !user) return;
    try {
      setIsDeletingExtension(true);
      await editExtension({
        currentUser: user,
        spaceId,
        assignment: { ...extensionForDeletion, status: 'trash' },
        accessToken,
      });
    } catch (err) {
      throw err;
    } finally {
      setIsDeletingExtension(false);
      setOpenDeleteExtensionModal(false);
      closeMenu();
    }
  };

  const isStaffAccess = useSelector(accessingCurrentSpaceAsStaff);
  const canEditExtensionsForAccess = canEditExtensions && !isStaffAccess;

  return (
    <>
      <Box sx={styles.sidebarWrapper}>
        <Box sx={styles.sidebarNavigation}>
          <NavigationBackHeader route={backRoute} />
        </Box>
        <Box sx={styles.sidebarHeader}>
          <Tooltip
            title={assignmentTitle}
            disableFocusListener={!isMdUp || !isTitleOverflowing}
            disableHoverListener={!isMdUp || !isTitleOverflowing}
            disableTouchListener
          >
            <Box sx={styles.assignmentTitle} ref={setTitleRef}>
              {assignmentTitle}
            </Box>
          </Tooltip>
          {canCreateExtensions && !isStaffAccess && (
            <Button
              type="button"
              onClick={() => {
                setSelectedExtension(undefined);
                setIsOpen(true);
              }}
              sx={styles.newExtension}
            >
              <Icons.Add />
              New extension
            </Button>
          )}
        </Box>
        <Box component="ul" sx={styles.sidebarContent}>
          {extensions && extensions.length > 0 && (
            <Box sx={styles.extensions}>
              {extensions?.map((extension) => {
                const isExtension = Boolean(extension.parentAssignmentId);
                return (
                  <ExtensionItem
                    key={extension.id}
                    extension={extension}
                    openMenu={openMenu}
                    editExtension={editExtension}
                    canEditExtensions={canEditExtensionsForAccess}
                    selectedExtensionId={selectedExtensionId}
                    accessToken={accessToken}
                    spaceId={spaceId}
                    styles={styles}
                    menuExtension={menuExtension}
                    isExtension={isExtension}
                    onSelect={onSelectExtension}
                  />
                );
              })}
            </Box>
          )}
        </Box>
        <Menu
          id="extension-options-menu"
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={closeMenu}
          aria-labelledby={
            menuExtension ? `extension-options-${menuExtension.id}` : undefined
          }
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            style: {
              backgroundColor: isDark ? '#363636' : undefined,
              color: isDark ? '#ffffff' : undefined,
              marginTop: '4px',
              width: 'max-content',
            },
          }}
          sx={styles.extensionMenu}
        >
          <MenuItem
            icon={<Icons.EditOutlined sx={{ fontSize: 24 }} />}
            label="Edit"
            onClick={openEditExtension}
          />
          <MenuItem
            icon={<Icons.DeleteOutlined sx={{ fontSize: 24 }} />}
            label="Delete"
            onClick={() => {
              setOpenDeleteExtensionModal(true);
            }}
          />
        </Menu>
        {openDeleteExtensionModal && menuExtension && (
          <DeleteExtensionModal
            styles={styles}
            showLoader={isDeletingExtension}
            open={openDeleteExtensionModal}
            onClose={() => setOpenDeleteExtensionModal(false)}
            onConfirm={handleDeleteExtension}
          />
        )}
      </Box>
      {assignment &&
        user &&
        (canCreateExtensions || canEditExtensionsForAccess) && (
          <ManageExtension
            accessToken={accessToken}
            assignment={assignment}
            assignmentCreationError={assignmentCreationError}
            assignmentUpdateError={assignmentUpdateError}
            createExtension={createExtension}
            editExtension={editExtension}
            extension={selectedExtension}
            isOpen={isOpen}
            loadStudents={loadStudents}
            onExtensionSaved={onExtensionSaved}
            onCloseModal={closeExtension}
            resetExtensionErrors={resetExtensionErrors}
            spaceId={spaceId}
            user={user}
          />
        )}
    </>
  );
};

export default SubmissionSidebar;

const DeleteExtensionModal = ({
  styles,
  showLoader,
  open,
  onClose,
  onConfirm,
}: {
  styles: any;
  showLoader: boolean;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  return (
    <Modal open={open} onClose={showLoader ? undefined : onClose}>
      <Box sx={styles.modal}>
        <Box sx={styles.header}>
          <Typography sx={styles.title} id="delete-extension" tabIndex={0}>
            {EXTENSION_DELETE_CONFIRMATION_TITLE}
          </Typography>
          <IconButton
            aria-label="Close delete extension dialog"
            onClick={onClose}
            sx={styles.closeButton}
            disabled={showLoader}
          >
            <Tooltip title="Close">
              <Icons.Close />
            </Tooltip>
          </IconButton>
        </Box>

        <Typography component="div" sx={styles.body} tabIndex={0}>
          {SURE_DELETE_EXTENSION_RESPONSIVE}{' '}
        </Typography>

        <Box sx={styles.footer}>
          <Button
            kind="secondary"
            type="button"
            sx={styles.buttonSx}
            onClick={onClose}
            disabled={showLoader}
          >
            {CANCEL_EXTENSION}
          </Button>
          <Button
            kind="primary"
            type="button"
            sx={styles.buttonSx}
            onClick={onConfirm}
            disabled={showLoader}
            loading={showLoader}
          >
            {DELETE_EXTENSION}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
